import { LitElement, html, css, classMap, utils } from '@hh/global.js'
const { debounce } = utils

class HHPredictiveSearch extends LitElement {
  static properties = {
    open: {
      type: Boolean,
      reflect: true,
    },
    openerSelector: {
      type: String,
      attribute: 'opener-selector',
    },
    searchTerm: {
      type: String,
    },
    searchActive: {
      type: Boolean,
      attribute: 'active',
      reflect: true,
    },
    noResults: {
      type: Boolean,
      attribute: 'no-results',
      reflect: true,
    },
    cachedResults: {
      type: Object,
    },
    formStyle: {
      type: String,
      attribute: 'form-style',
    },
  }

  constructor() {
    super()

    this.debounceSearchInput = debounce(this.onSearchInput.bind(this), 500)

    this.cachedResults = {}

    this.classes = {
      hidden: 'hidden',
    }

    this.minChars = 2

    // https://shopify.dev/docs/api/ajax/reference/predictive-search
    this.resourceTypes = ['product', 'query', 'page', 'collection']

    this.searchFields = [
      'product_type',
      'tag',
      'title',
      'variants.barcode',
      'variants.sku',
      'variants.title',
      'vendor',
    ]
  }

  get opener() {
    return document.querySelector(this.openerSelector) ?? null
  }

  get form() {
    return (
      this.shadowRoot
        .querySelector('slot[name="search-form"]')
        ?.assignedNodes()[0] ?? null
    )
  }

  get searchInput() {
    return this.querySelector('#Search-In-Modal')
  }

  get searchResults() {
    return (
      this.shadowRoot
        .querySelector('slot[name="search-results"]')
        ?.assignedNodes()[0] ?? null
    )
  }

  connectedCallback() {
    super.connectedCallback()
  }

  disconnectedCallback() {
    super.disconnectedCallback()
  }

  firstUpdated() {
    this.searchInput.addEventListener('input', this.debounceSearchInput)
    this.searchInput.value = this.searchTerm ?? ''
  }

  updated(changedProperties) {
    if (changedProperties.has('searchTerm')) {
      this.searchChange(this.searchTerm)
    }
  }

  onSearchInput(e) {
    if (!this.searchInput) return

    const { value } = e.target

    if (value.length === 0) {
      document.querySelector('.js-suggested-terms').classList.remove('hidden')

      if (this.searchResults) this.searchResults.innerHTML = ''
      this.refreshFocusTrap()
    }

    this.searchTerm = value
  }

  searchChange(query) {
    const newSearchTerm = query.trim()
    if (newSearchTerm.length < this.minChars) {
      this.searchActive = false
      return
    }

    this.searchActive = true
    this.getSearchResults(newSearchTerm)
  }

  async getSearchResults(query) {
    const queryKey = query.replaceAll(' ', '-').toLowerCase()
    this.classList.add('fade-out')

    if (this.cachedResults?.[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey])
      return
    }

    const searchEndpoint = window.routes.predictive_search_url

    const params = {
      q: query,
      'resources[type]': this.resourceTypes.join(','),
      'resources[options][fields]': this.searchFields.join(','),
      section_id: 'predictive-search',
    }

    const paramsString = new URLSearchParams(params).toString()
    const searchUrl = `${searchEndpoint}?${paramsString}`

    try {
      const res = await fetch(searchUrl)
      if (!res.ok) throw new Error(res.status)

      const text = await res.text()
      const resultsMarkup = new DOMParser()
        .parseFromString(text, 'text/html')
        .querySelector('#shopify-section-predictive-search')

      this.cachedResults[queryKey] = resultsMarkup

      this.renderSearchResults(resultsMarkup)
    } catch (error) {
      console.error('An error occurred in the predictive search request: ', {
        error,
        query,
        searchUrl,
      })
    }
  }

  renderSearchResults(resultsMarkup) {
    this.noResults = resultsMarkup.querySelector('.js-no-results') !== null
    document.querySelector('.js-suggested-terms').classList.add('hidden')

    resultsMarkup.querySelectorAll('[data-search-term]').forEach((el) => {
      el.innerHTML = el.innerHTML.replace(
        new RegExp(this.searchTerm, 'gi'),
        (match) => `<mark>${match}</mark>`
      )
    })
    this.searchResults.innerHTML = resultsMarkup.innerHTML
    this.refreshFocusTrap()
    document
      .querySelectorAll('[data-featured-link]')
      .forEach((nonAnchorLink) => {
        nonAnchorLink.addEventListener('click', () => {
          const link = nonAnchorLink.getAttribute('data-featured-link')
          window.location.href = link
        })
      })
    const results_text = resultsMarkup.querySelector('.js-results-count-value')
    if (results_text) {
      document.querySelector('.js-results-count').innerHTML =
        results_text.innerHTML
    }
    setTimeout(() => {
      this.classList.remove('fade-out')
    }, 100)
  }

  refreshFocusTrap(timeout = 0) {
    setTimeout(() => {
      this.dispatchEvent(
        new CustomEvent('focus-trap:refresh', { bubbles: true, composed: true })
      )
    }, timeout)
  }

  render() {
    return html`
      <div role="region" aria-label="product filter">
        <div class="search-form-wrapper">
          <slot name="search-form"></slot>
        </div>

        <div
          class=${classMap({
            'search-results-container': true,
            active: this.searchActive,
          })}
          aria-hidden="${!this.searchActive}"
          ?inert=${!this.searchActive}
        >
          <div class="search-results-inner">
            <slot name="search-results"></slot>
          </div>
        </div>
      </div>
    `
  }

  static styles = css`
    :host *,
    :host *::before,
    :host *::after {
      box-sizing: border-box;
    }

    .search-results-container {
      display: none;
    }

    .search-results-container.active {
      display: block;
    }

    :host([no-results]) .search-results-container.active {
      overflow-y: hidden;
      touch-action: none;
    }
  `
}

customElements.define('hh-predictive-search', HHPredictiveSearch)
